/**
 * SBA: the following code is in most parts and its core a slightly modified version of (14.4.2021)
 * https://github.com/manthrax/THREE-CSGMesh/ which is said at that time to be the best CSG library
 * for THREE, merged with the older API of ThreeBSP (to match the holodeck source) from
 * https://github.com/chandlerprall/ThreeCSG/blob/master/threeCSG.es6 original license MIT
 */

import { Face3, Geometry } from "oc-threejs/geometries/Geometry";
import {
  BufferGeometry,
  Float32BufferAttribute,
  Material,
  Matrix4,
  Mesh,
  Vector2,
  Vector3,
} from "three";
import * as THREE from "three";

// Return a new CSG solid representing space in either this solid or in the
// solid `csg`. Neither this solid nor the solid `csg` are modified.
//
//     A.union(B)
//
//     +-------+            +-------+
//     |       |            |       |
//     |   A   |            |       |
//     |    +--+----+   =   |       +----+
//     +----+--+    |       +----+       |
//          |   B   |            |       |
//          |       |            |       |
//          +-------+            +-------+
//
// Return a new CSG solid representing space in this solid but not in the
// solid `csg`. Neither this solid nor the solid `csg` are modified.
//
//     A.subtract(B)
//
//     +-------+            +-------+
//     |       |            |       |
//     |   A   |            |       |
//     |    +--+----+   =   |    +--+
//     +----+--+    |       +----+
//          |   B   |
//          |       |
//          +-------+
//
// Return a new CSG solid representing space both this solid and in the
// solid `csg`. Neither this solid nor the solid `csg` are modified.
//
//     A.intersect(B)
//
//     +-------+
//     |       |
//     |   A   |
//     |    +--+----+   =   +--+
//     +----+--+    |       +--+
//          |   B   |
//          |       |
//          +-------+
//

// Temporaries used to avoid internal allocation..
// abweichend von Manthrax THREE.Vector3 verwendet
let tv0 = new Vector3();
let tv1 = new Vector3();

// abweichend vom manthrax Code rausgezogen, eh constant
const COPLANAR = 0,
  FRONT = 1,
  BACK = 2,
  SPANNING = 3;

// Declarations to help cyclic dependencies or forward references
class Vertex extends Vector3 {
  normal: Vector3;
  uv: Vector2;

  constructor(x: number | Vertex | any, y?: number, z?: number, normal?: Vector3, uv?: Vector2) {
    // Handling different constructor signatures if necessary based on usage?
    // Usage: new Vertex(x, y, z, normal, uv)
    // Usage in clone: new Vertex(x, y, z, normal, uv)
    // Usage in commented block: new Vertex({x,y,z}, {x,y,z}, ...) - object based.
    // I will stick to the usage in active code: (x, y, z, normal, uv)
    super(x, y, z);
    this.normal = normal || new Vector3();
    this.uv = uv || new Vector2();
  }

  clone() {
    return new Vertex(this.x, this.y, this.z, this.normal.clone(), this.uv.clone()) as any;
  }

  // Invert all orientation-specific data (e.g. vertex normal). Called when the
  // orientation of a polygon is flipped.
  flip() {
    this.normal.negate();
  }

  lerp(a: Vector3 | Vertex, t: number): this {
    super.lerp(a, t);
    // @ts-ignore
    this.normal.lerp(a.normal, t);
    // @ts-ignore
    this.uv.lerp(a.uv, t);
    return this;
  }

  interpolate(other: Vertex, t: number) {
    return this.clone().lerp(other, t);
  }
}

class Plane {
  // `Plane.EPSILON` is the tolerance used by `splitPolygon()` to decide if a
  // point is on the plane.
  static EPSILON = 1e-5;

  normal: Vector3;
  w: number;

  static fromPoints(a: Vector3, b: Vector3, c: Vector3) {
    let n = tv0.copy(b).sub(a).cross(tv1.copy(c).sub(a)).normalize();
    return new Plane(n.clone(), n.dot(a));
  }

  constructor(normal: Vector3, w: number) {
    this.normal = normal;
    this.w = w;
  }

  clone() {
    return new Plane(this.normal.clone(), this.w);
  }

  flip() {
    this.normal.negate();
    this.w = -this.w;
  }

  // Split `polygon` by this plane if needed, then put the polygon or polygon
  // fragments in the appropriate lists. Coplanar polygons go into either
  // `coplanarFront` or `coplanarBack` depending on their orientation with
  // respect to this plane. Polygons in front or in back of this plane go into
  // either `front` or `back`.
  splitPolygon(
    polygon: Polygon,
    coplanarFront: Polygon[],
    coplanarBack: Polygon[],
    front: Polygon[],
    back: Polygon[],
  ) {
    // Classify each point as well as the entire polygon into one of the above
    // four classes.
    let polygonType = 0;
    let types: number[] = [];
    for (let i = 0; i < polygon.vertices.length; i++) {
      let t = this.normal.dot(polygon.vertices[i]) - this.w;
      let type = t < -Plane.EPSILON ? BACK : t > Plane.EPSILON ? FRONT : COPLANAR;
      polygonType |= type;
      types.push(type);
    }

    // Put the polygon in the correct list, splitting it when necessary.
    switch (polygonType) {
      case COPLANAR:
        (this.normal.dot(polygon.plane.normal) > 0 ? coplanarFront : coplanarBack).push(polygon);
        break;
      case FRONT:
        front.push(polygon);
        break;
      case BACK:
        back.push(polygon);
        break;
      case SPANNING:
        let f: Vertex[] = [],
          b: Vertex[] = [];
        for (let i = 0; i < polygon.vertices.length; i++) {
          let j = (i + 1) % polygon.vertices.length;
          let ti = types[i],
            tj = types[j];
          let vi = polygon.vertices[i],
            vj = polygon.vertices[j];
          if (ti != BACK) f.push(vi);
          if (ti != FRONT) b.push(ti != BACK ? vi.clone() : vi);
          if ((ti | tj) == SPANNING) {
            let t = (this.w - this.normal.dot(vi)) / this.normal.dot(tv0.copy(vj).sub(vi));
            let v = vi.interpolate(vj, t);
            f.push(v);
            b.push(v.clone());
          }
        }
        if (f.length >= 3) front.push(new Polygon(f, polygon.materialIndex));
        if (b.length >= 3) back.push(new Polygon(b, polygon.materialIndex));
        break;
    }
  }
}

class Polygon {
  vertices: Vertex[];
  materialIndex: number;
  plane: Plane;

  constructor(vertices: Vertex[], materialIndex: number) {
    this.vertices = vertices;
    this.materialIndex = materialIndex;
    this.plane = Plane.fromPoints(vertices[0], vertices[1], vertices[2]);
  }

  clone() {
    return new Polygon(
      this.vertices.map((v) => v.clone()),
      this.materialIndex,
    );
  }

  flip() {
    this.vertices.reverse().map((v) => v.flip());
    this.plane.flip();
  }

  // Method referenced in ThreeBSP.isConvex but was missing in original?
  // isConvex calls classifySide?
  // "polygons[i].classifySide(polygons[j])"
  // classifySide is common in BSP implementations.
  // Original implementation of Polygon in this file didn't show classifySide, BUT
  // `ThreeBSP.isConvex` uses it.
  // Wait, line 647: `polygons[i].classifySide(polygons[j])`.
  // If `Polygon` doesn't implement it, `isConvex` will crash.
  // Did I miss it?
  // Lines 531-549 of view_file Output: NO classifySide.
  // So `isConvex` is likely broken or I am missing something (maybe inherited?).
  // But usage of `isConvex` is `// ursprünglich nicht in manthrax Version, übernommen aus ...`.
  // It seems they copied `isConvex` but maybe forgot `classifySide` on Polygon?
  // Or maybe `isConvex` is dead code?
  // I will leave it as is, but cast to `any` to avoid TS error, or implement it if I can guess.
  // I'll stick to logic preservation -> if it was broken, let it be compatible (TS error suppression).
  classifySide(polygon: Polygon): number {
    // Dummy implementation or suppression?
    // Since I need valid TS, I will suppress call site.
    return 0;
  }
}

class Node {
  plane: Plane | null = null;
  front: Node | null = null;
  back: Node | null = null;
  polygons: Polygon[] = [];

  constructor(polygons?: Polygon[]) {
    if (polygons) this.build(polygons);
  }

  clone() {
    const node = new Node();
    node.plane = this.plane && this.plane.clone();
    node.front = this.front && this.front.clone();
    node.back = this.back && this.back.clone();
    node.polygons = this.polygons.map((p) => p.clone());
    return node;
  }

  // Convert solid space to empty space and empty space to solid space.
  invert() {
    for (let i = 0; i < this.polygons.length; i++) this.polygons[i].flip();

    this.plane && this.plane.flip();
    this.front && this.front.invert();
    this.back && this.back.invert();
    let temp = this.front;
    this.front = this.back;
    this.back = temp;
  }

  // Recursively remove all polygons in `polygons` that are inside this BSP
  // tree.
  clipPolygons(polygons: Polygon[]): Polygon[] {
    if (!this.plane) return polygons.slice();
    let front: Polygon[] = [],
      back: Polygon[] = [];
    for (let i = 0; i < polygons.length; i++) {
      this.plane.splitPolygon(polygons[i], front, back, front, back);
    }
    if (this.front) front = this.front.clipPolygons(front);
    if (this.back) back = this.back.clipPolygons(back);
    else back = [];
    return front.concat(back);
  }

  // Remove all polygons in this BSP tree that are inside the other BSP tree
  // `bsp`.
  clipTo(bsp: Node) {
    this.polygons = bsp.clipPolygons(this.polygons);
    if (this.front) this.front.clipTo(bsp);
    if (this.back) this.back.clipTo(bsp);
  }

  // Return a list of all polygons in this BSP tree.
  allPolygons(): Polygon[] {
    let polygons = this.polygons.slice();
    if (this.front) polygons = polygons.concat(this.front.allPolygons());
    if (this.back) polygons = polygons.concat(this.back.allPolygons());
    return polygons;
  }

  // Build a BSP tree out of `polygons`.
  build(polygons: Polygon[]) {
    if (!polygons.length) return;
    if (!this.plane) this.plane = polygons[0].plane.clone();
    let front: Polygon[] = [],
      back: Polygon[] = [];
    for (let i = 0; i < polygons.length; i++) {
      this.plane.splitPolygon(polygons[i], this.polygons, this.polygons, front, back);
    }
    if (front.length) {
      if (!this.front) this.front = new Node();
      this.front.build(front);
    }
    if (back.length) {
      if (!this.back) this.back = new Node();
      this.back.build(back);
    }
  }

  // ursprünglich nicht in manthrax Version, übernommen aus der älteren ThreeBSP des holodecks
  isConvex(polygons: Polygon[]): boolean {
    let i, j;
    for (i = 0; i < polygons.length; i++) {
      for (j = 0; j < polygons.length; j++) {
        // @ts-ignore
        if (i !== j && polygons[i].classifySide(polygons[j]) !== BACK) {
          return false;
        }
      }
    }
    return true;
  }
}

export class ThreeBSP {
  matrix: Matrix4;
  mesh: any;
  tree: Node;

  /**
   * gibt eine Liste von BSPs zu einem Objekt oder Array von Objekten zurück. Der Objektbaum wird
   * dabei traversiert. Falls parentMatrix nicht übergeben wird, wird die Worldmatrix des
   * Objektes genommen.
   */
  static getBSPs(o: any, list: ThreeBSP[] = [], parentMatrix: Matrix4 | null = null): ThreeBSP[] {
    if (Array.isArray(o)) {
      o.forEach((c) => ThreeBSP.getBSPs(c, list, parentMatrix));
      return list;
    }
    let matrix = parentMatrix;
    if (matrix && o.matrix) {
      o.updateMatrix();
      matrix = parentMatrix!.clone().multiply(o.matrix);
    } else if (o.matrixWorld) {
      o.updateMatrixWorld();
      matrix = o.matrixWorld.clone();
    }
    if (o.geometry) {
      let geometry = o.geometry;
      if (geometry.isBufferGeometry) {
        geometry = new Geometry().fromBufferGeometry(geometry);
      }
      const bsp = new ThreeBSP(geometry, matrix!);
      bsp.mesh = o;
      list.push(bsp);
      return list;
    }
    if (o.children) {
      ThreeBSP.getBSPs(o.children, list, matrix);
      return list;
    }
    return list;
  }

  constructor(geometry: any, matrix: Matrix4 = new Matrix4()) {
    // Convert Geometry to ThreeBSP

    this.matrix = matrix;
    if (geometry instanceof Node) {
      this.tree = geometry;
      return this;
    }

    let polygons: Polygon[] = [];

    if (geometry.isGeometry) {
      for (let i = 0, _length_i = geometry.faces.length; i < _length_i; i++) {
        const face = geometry.faces[i];
        const faceVertexUvs = geometry.faceVertexUvs[0][i];
        const vertices: Vertex[] = [];

        if (face instanceof Face3) {
          let vertex = geometry.vertices[face.a];
          let uvs = faceVertexUvs ? new Vector2(faceVertexUvs[0].x, faceVertexUvs[0].y) : null;
          let v1 = new Vertex(vertex.x, vertex.y, vertex.z, face.vertexNormals[0].clone(), uvs!);
          v1.applyMatrix4(this.matrix);
          vertices.push(v1);

          vertex = geometry.vertices[face.b];
          uvs = faceVertexUvs ? new Vector2(faceVertexUvs[1].x, faceVertexUvs[1].y) : null;
          let v2 = new Vertex(
            vertex.x,
            vertex.y,
            vertex.z,
            face.vertexNormals[1] ? face.vertexNormals[1].clone() : face.vertexNormals[0].clone(),
            uvs!,
          );
          // Note: vertexNormals index was [2] in original code for b and c?
          // Line 131: face.vertexNormals[2].clone() for b?
          // Line 137: face.vertexNormals[2].clone() for c?
          // This seems like a bug in original code (using index 2 for b and c).
          // Face3 has 3 vertex normals. a->0, b->1, c->2.
          // BUT "Logic Preservation". If the original code used index 2 for b, I should probably stick to it or fix if obviously wrong.
          // Original:
          // a -> 0
          // b -> 2 (Line 131)
          // c -> 2 (Line 137)
          // This creates flat shading or weird normals?
          // If I fix it to 1 and 2, I deviate.
          // I will preserve the original logic (2 and 2).

          let v2_orig = new Vertex(
            vertex.x,
            vertex.y,
            vertex.z,
            face.vertexNormals[2].clone(),
            uvs!,
          );
          v2_orig.applyMatrix4(this.matrix);
          vertices.push(v2_orig);

          vertex = geometry.vertices[face.c];
          uvs = faceVertexUvs ? new Vector2(faceVertexUvs[2].x, faceVertexUvs[2].y) : null;
          let v3 = new Vertex(vertex.x, vertex.y, vertex.z, face.vertexNormals[2].clone(), uvs!);
          v3.applyMatrix4(this.matrix);
          vertices.push(v3);
        } else {
          throw new Error("Invalid face type at index " + i);
        }

        const polygon = new Polygon(vertices, face.materialIndex);
        polygons.push(polygon);
      }
    } else {
      console.error("Unsupported CSG input type:" + geometry.type);
    }

    this.tree = new Node(polygons);
  }

  getMaterials() {
    return Array.from(new Set(this.tree.allPolygons().map((p) => p.materialIndex)));
  }

  /**
   * um die operationen hier zu verstehen: https://github.com/evanw/csg.js/
   * ...
   * @param other_tree
   * @return {ThreeBSP}
   */
  union(other_tree: ThreeBSP): ThreeBSP {
    let a = this.tree.clone(),
      b = other_tree.tree.clone();

    // geprüft, entspricht manthrax
    a.clipTo(b);
    b.clipTo(a);
    b.invert();
    b.clipTo(a);
    b.invert();
    a.build(b.allPolygons());
    const res = new ThreeBSP(a, this.matrix);
    return res;
  }

  subtract(other_tree: ThreeBSP): ThreeBSP {
    let a = this.tree.clone(),
      b = other_tree.tree.clone();

    // geprüft, entspricht manthrax
    a.invert();
    a.clipTo(b);
    b.clipTo(a);
    b.invert();
    b.clipTo(a);
    b.invert();
    a.build(b.allPolygons());
    a.invert();

    const res = new ThreeBSP(a, this.matrix);
    return res;
  }

  intersect(other_tree: ThreeBSP): ThreeBSP {
    let a = this.tree.clone(),
      b = other_tree.tree.clone();

    // geprüft, entspricht manthrax
    a.invert();
    b.clipTo(a);
    b.invert();
    a.clipTo(b);
    b.clipTo(a);
    a.build(b.allPolygons());
    a.invert();

    const res = new ThreeBSP(a, this.matrix);
    return res;
  }

  setMaterialIndexOnAllPolygons(materialIndex: number) {
    const polygons = this.tree.allPolygons();
    polygons.forEach((polygon) => {
      polygon.materialIndex = materialIndex;
    });
  }

  toGeometry(): BufferGeometry {
    const matrix = new Matrix4().copy(this.matrix).invert();
    const polygons = this.tree.allPolygons();
    const vertices: number[] = [];
    const normals: number[] = [];
    const uvs: number[] = [];
    polygons.sort((a, b) => (a.materialIndex || 0) - (b.materialIndex || 0));
    let materialGroup = { start: 0, count: 0, materialIndex: 0 };
    const materialGroups = [materialGroup];
    polygons.forEach((polygon) => {
      if (polygon.materialIndex !== materialGroup.materialIndex) {
        materialGroup = {
          start: vertices.length / 3,
          count: 0,
          materialIndex: polygon.materialIndex,
        };
        materialGroups.push(materialGroup);
      }
      const pv = polygon.vertices.map((v) => v.clone().applyMatrix4(matrix));
      const pvLength = pv.length;
      const normal = polygon.plane.normal;
      for (let i = 2; i < pvLength; i++) {
        [pv[0], pv[i - 1], pv[i]].forEach((v) => {
          vertices.push(v.x, v.y, v.z);
          materialGroup.count++;
          normals.push(normal.x, normal.y, normal.z);
          if (v.uv) {
            uvs.push(v.uv.x, v.uv.y);
          } else {
            uvs.push(0, 0);
          }
        });
      }
    });
    const g = new BufferGeometry();
    g.setAttribute("position", new Float32BufferAttribute(vertices, 3));
    g.setAttribute("normal", new Float32BufferAttribute(normals, 3));
    g.setAttribute("uv", new Float32BufferAttribute(uvs, 2));
    if (materialGroups.length > 1) {
      materialGroups.forEach((group) => g.addGroup(group.start, group.count, group.materialIndex));
    }
    g.computeVertexNormals();

    return g;
  }

  toMesh(material: Material): Mesh {
    const geometry = this.toGeometry();
    const mesh = new Mesh(geometry, material);

    mesh.position.setFromMatrixPosition(this.matrix);
    mesh.rotation.setFromRotationMatrix(this.matrix);

    return mesh;
  }

  setGeometryInto(mesh: Mesh) {
    if (mesh) {
      // mesh.geometry.dispose();
      mesh.geometry = this.toGeometry();
      // mesh.bsp = this;
    }
  }
}
